import { useEffect, useState } from 'react';
import {
  CatSkeleton,
  CatTable,
  CatTableBody,
  CatTableCell,
  CatTableHeader,
  CatTableHeaderCell,
  CatTableObject,
  CatTableRow
} from 'components/blocks-components';
import { SavedList } from 'services/savedLists/types';
import { TableMeta } from './types';
import {
  RowSelectionState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { useSavedListColumn } from './hooks/useSavedListsColumn';
import { createPortal } from 'react-dom';
import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_SAVED_LISTS_PER_PAGE } from 'app/[lang]/[esite]/(core)/cart/_components/quick-order-section/quick-order-form/constants';
import { SavedListDrawerFooter } from '../saved-list-drawer-footer/saved-list-drawer-footer';
import { Conditional } from '@cat-ecom/pcc-components';
import styles from './saved-list-table.module.scss';

export interface SavedListTableProps {
  isLoading: boolean;
  isCspUser: boolean;
  shoppingLists: SavedList[];
  isMobile?: boolean;
  onClose: () => void;
  onActionDrawer?: (rowSelected: RowSelectionState) => void;
  confirmBtnText: string;
  disableHighlightOnHover?: boolean;
  savedListDetailFeatureFlag?: boolean;
  showAllList?: boolean;
}

export const SavedListTable = ({
  isLoading,
  isCspUser,
  shoppingLists,
  isMobile = false,
  onClose,
  onActionDrawer,
  confirmBtnText,
  disableHighlightOnHover = false,
  savedListDetailFeatureFlag = false,
  showAllList = false
}: Readonly<SavedListTableProps>) => {
  const [footerSelector, setFooterSelector] = useState(null);
  const [footerSelectorMobile, setFooterSelectorMobile] = useState(null);

  useEffect(() => {
    const footerSelectorVar = document
      ? document.getElementById('saved-list-footer-container')
      : null;
    const footerSelectorMobileVar = document
      ? document.getElementById('saved-list-footer-container-mobile')
      : null;
    setFooterSelector(footerSelectorVar);
    setFooterSelectorMobile(footerSelectorMobileVar);
  }, []);

  const columns = useSavedListColumn(
    isCspUser,
    isMobile,
    savedListDetailFeatureFlag
  );

  const notEmptyShoppingLists = (shoppingLists ?? []).filter(
    (list) => list.orderItemsQuantity > 0
  );
  const finalLists = showAllList
    ? (shoppingLists ?? [])
    : (notEmptyShoppingLists ?? []);

  const [sorting, setSorting] = useState<SortingState>(
    !isMobile
      ? [
          {
            id: 'updated',
            desc: true
          }
        ]
      : []
  );

  const table = useReactTable({
    data: finalLists,
    columns,
    manualPagination: true,
    enableSorting: true,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    enableMultiRowSelection: false,
    getRowId: (row) => row.shoppingListId.toString(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting
    },
    onSortingChange: setSorting
  });
  const startIndex =
    table.getState().pagination.pageIndex * DEFAULT_SAVED_LISTS_PER_PAGE;
  const paginatedPosts = table
    .getRowModel()
    .rows.slice(startIndex, startIndex + DEFAULT_SAVED_LISTS_PER_PAGE);

  const loadingTable = (
    <div className="w-100 h-100">
      <CatSkeleton size="xxl" />
    </div>
  );

  return (
    <>
      <Conditional test={!isLoading} fallback={loadingTable}>
        <CatTableObject height="100%">
          <CatTable aria-label="saved-list-table">
            <CatTableHeader>
              {table.getHeaderGroups().map((headerGroup) => {
                return (
                  <CatTableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return !isMobile ? (
                        <CatTableHeaderCell
                          className={
                            disableHighlightOnHover
                              ? styles['saved-list-table__header-cell']
                              : ''
                          }
                          width={
                            (header.column.columnDef.meta as TableMeta)?.width
                          }
                          key={uuidv4()}
                          onBlColumnSort={header.column.getToggleSortingHandler()}
                          {...{
                            sortDirection: sorting?.find(
                              (item) => item.id === header.id
                            )?.desc
                              ? 'descending'
                              : 'ascending'
                          }}
                          {...((header.column.columnDef.meta as TableMeta)
                            ?.sort && {
                            behavior: 'sortable'
                          })}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </CatTableHeaderCell>
                      ) : (
                        <CatTableHeaderCell
                          key={`saved-list-header-${uuidv4()}`}
                          className="d-none"
                        />
                      );
                    })}
                  </CatTableRow>
                );
              })}
            </CatTableHeader>
            <CatTableBody>
              {paginatedPosts.map((row) => {
                return (
                  <CatTableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <CatTableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </CatTableCell>
                      );
                    })}
                  </CatTableRow>
                );
              })}
            </CatTableBody>
          </CatTable>
        </CatTableObject>
      </Conditional>
      {footerSelector &&
        !isMobile &&
        createPortal(
          <div className="w-100 d-none d-md-flex">
            <SavedListDrawerFooter
              table={table}
              onCloseDrawer={onClose}
              onActionDrawer={onActionDrawer}
              confirmBtnText={confirmBtnText}
            />
          </div>,
          footerSelector
        )}
      {footerSelectorMobile &&
        isMobile &&
        createPortal(
          <div className="w-100 d-flex d-md-none">
            <SavedListDrawerFooter
              table={table}
              onCloseDrawer={onClose}
              onActionDrawer={onActionDrawer}
              confirmBtnText={confirmBtnText}
            />
          </div>,
          footerSelectorMobile
        )}
    </>
  );
};
